import api from "@/app/api";
import { OfflineDatabase } from "./database";
import { STORES, type OfflineDBMetadata } from "./schema";

/**
 * State stored in IndexedDB
 */
interface NumberState {
  lastGeneratedNumber: number;
  companyId: string;
  userId: string;
  updatedAt: number;
}

/**
 * Robust Pre-Order Number Manager
 *
 * A singleton manager that generates unique pre-order numbers client-side.
 * Uses three-tier storage: In-memory → IndexedDB → Backend (feature flags)
 *
 * Algorithm: number = counter * mod + offset
 * - mod: Company-wide setting (e.g., 10)
 * - offset: User-specific (0 to mod-1) - ensures uniqueness across users
 *
 * Example with mod=10:
 * - User offset=3: 10003 → 10013 → 10023...
 * - User offset=5: 10005 → 10015 → 10025...
 */
export class PreOrderNumberManager {
  private static instance: PreOrderNumberManager | null = null;
  private static instanceKey: string | null = null;

  // State
  private counter: number = 0;
  private lastGeneratedNumber: number = 0;
  private mod: number;
  private offset: number;
  private companyId: string;
  private userId: string;
  private dirty: boolean = false;
  private lastBackendSync: number = 0;
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  // Services
  private db: OfflineDatabase;

  // Timers
  private syncIntervalId: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;

  // Config
  private static readonly STORAGE_KEY = "preOrderNumberState";
  private static readonly CONFIG_CACHE_KEY = "preOrderNumberConfig";
  private static readonly SYNC_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
  private static readonly CONFIG_CACHE_TTL = 3600000; // 1 hour in ms

  private constructor(
    mod: number,
    offset: number,
    companyId: string,
    userId: string,
  ) {
    this.mod = mod;
    this.offset = offset;
    this.companyId = companyId;
    this.userId = userId;
    this.db = new OfflineDatabase();
  }

  /**
   * Get or create the singleton instance.
   *
   * If called with different parameters (different company/user), the existing
   * instance is destroyed and a new one is created.
   */
  static getInstance(
    mod: number,
    offset: number,
    companyId: string,
    userId: string,
  ): PreOrderNumberManager {
    const key = `${companyId}:${userId}:${mod}:${offset}`;

    // If parameters changed, destroy old instance
    if (
      PreOrderNumberManager.instance &&
      PreOrderNumberManager.instanceKey !== key
    ) {
      PreOrderNumberManager.instance.destroy();
      PreOrderNumberManager.instance = null;
      PreOrderNumberManager.instanceKey = null;
    }

    // Create new instance if needed
    if (!PreOrderNumberManager.instance) {
      PreOrderNumberManager.instance = new PreOrderNumberManager(
        mod,
        offset,
        companyId,
        userId,
      );
      PreOrderNumberManager.instanceKey = key;
    }

    return PreOrderNumberManager.instance;
  }

  /**
   * Check if an instance exists
   */
  static hasInstance(): boolean {
    return PreOrderNumberManager.instance !== null;
  }

  /**
   * Get existing instance without creating new one
   */
  static getExistingInstance(): PreOrderNumberManager | null {
    return PreOrderNumberManager.instance;
  }

  /**
   * Initialize the manager by loading state from all sources.
   *
   * Priority for starting counter:
   * 1. Local IndexedDB state (if same company/user)
   * 2. Backend last synced value
   * 3. Max pre_order_number from database
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    try {
      await this.db.init();

      // Try loading cached config first (offline-first)
      const cachedConfig = await this.loadCachedConfig();

      if (cachedConfig && !this.isConfigStale(cachedConfig)) {
        // Use cached config
        this.offset = cachedConfig.offset;
        this.mod = cachedConfig.mod;
        this.counter = cachedConfig.start_counter;

        // Refresh in background (fire and forget)
        this.refreshConfigInBackground().catch(() => {});
      } else {
        // Fetch fresh config from server
        const serverConfig = await api.preOrders.getNumberConfig();

        this.offset = serverConfig.offset;
        this.mod = serverConfig.mod;
        this.counter = serverConfig.start_counter;

        // Cache for future use
        await this.saveCachedConfig(serverConfig);
      }

      // Load local state for counter continuation
      const localState = await this.loadLocalState();
      if (localState && localState.lastGeneratedNumber > 0) {
        const calculatedCounter =
          Math.floor(
            (localState.lastGeneratedNumber - this.offset) / this.mod,
          ) + 1;

        this.counter = Math.max(this.counter, calculatedCounter);
        this.lastGeneratedNumber = localState.lastGeneratedNumber;
      }

      this.startPeriodicSync();
      this.setupVisibilityHandler();
      this.setupPeriodicConfigRefresh();
      this.initialized = true;
    } catch (error) {
      // Try fallback to cached config even if expired
      const cachedConfig = await this.loadCachedConfig();
      if (cachedConfig) {
        this.offset = cachedConfig.offset;
        this.mod = cachedConfig.mod;
        this.counter = cachedConfig.start_counter;
        this.initialized = true;
      } else {
        throw error;
      }
    }
  }

  /**
   * Load state from IndexedDB
   */
  private async loadLocalState(): Promise<NumberState | null> {
    try {
      const stored = await this.db.get<OfflineDBMetadata>(
        STORES.METADATA,
        PreOrderNumberManager.STORAGE_KEY,
      );

      if (!stored?.value) return null;

      const state = stored.value as NumberState;

      // Verify it's for the same company/user
      if (state.companyId !== this.companyId || state.userId !== this.userId) {
        return null;
      }

      return state;
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetch state from backend
   */
  private async fetchBackendState(): Promise<{
    maxNumber: number;
    lastSynced: number | null;
  }> {
    try {
      const response = await api.preOrders.getNumberState();
      return {
        maxNumber: response.max_number,
        lastSynced: response.last_synced,
      };
    } catch (error) {
      // Return safe defaults
      return { maxNumber: 0, lastSynced: null };
    }
  }

  /**
   * Load cached config from IndexedDB
   */
  private async loadCachedConfig(): Promise<any | null> {
    const cached = await this.db.get<OfflineDBMetadata>(
      STORES.METADATA,
      PreOrderNumberManager.CONFIG_CACHE_KEY,
    );
    return (cached?.value as any) || null;
  }

  /**
   * Save config to IndexedDB cache
   */
  private async saveCachedConfig(config: any): Promise<void> {
    await this.db.set(STORES.METADATA, {
      key: PreOrderNumberManager.CONFIG_CACHE_KEY,
      value: {
        ...config,
        cachedAt: Date.now(),
      },
      updatedAt: Date.now(),
    });
  }

  /**
   * Check if cached config is stale
   */
  private isConfigStale(cachedConfig: any): boolean {
    const age = Date.now() - (cachedConfig.cachedAt || 0);
    return age > PreOrderNumberManager.CONFIG_CACHE_TTL;
  }

  /**
   * Refresh config in background
   */
  private async refreshConfigInBackground(): Promise<void> {
    try {
      const serverConfig = await api.preOrders.getNumberConfig();

      // Update if changed
      if (
        serverConfig.offset !== this.offset ||
        serverConfig.mod !== this.mod
      ) {
        // Update runtime config
        this.offset = serverConfig.offset;
        this.mod = serverConfig.mod;

        // Recalculate counter
        this.counter = await this.recalculateCounter();
      }

      // Update cache
      await this.saveCachedConfig(serverConfig);
    } catch (error) {
      // Silently fail background refresh
    }
  }

  /**
   * Setup periodic config refresh
   */
  private setupPeriodicConfigRefresh(): void {
    // Refresh config every hour
    setInterval(() => {
      this.refreshConfigInBackground().catch(() => {});
    }, PreOrderNumberManager.CONFIG_CACHE_TTL);
  }

  /**
   * Recalculate counter from local state
   */
  private async recalculateCounter(): Promise<number> {
    const localState = await this.loadLocalState();
    if (localState && localState.lastGeneratedNumber > 0) {
      return (
        Math.floor((localState.lastGeneratedNumber - this.offset) / this.mod) +
        1
      );
    }
    return 1;
  }

  /**
   * Save state to IndexedDB
   */
  private async saveLocalState(): Promise<void> {
    try {
      const state: NumberState = {
        lastGeneratedNumber: this.lastGeneratedNumber,
        companyId: this.companyId,
        userId: this.userId,
        updatedAt: Date.now(),
      };

      await this.db.set<OfflineDBMetadata>(STORES.METADATA, {
        key: PreOrderNumberManager.STORAGE_KEY,
        value: state,
        updatedAt: Date.now(),
      });
    } catch (error) {
      // Silent failure - state will be recovered on next initialization
    }
  }

  /**
   * Generate the next pre-order number.
   *
   * @returns The next pre-order number as a string
   */
  async generateNext(): Promise<string> {
    // Ensure initialized
    if (!this.initialized) {
      await this.initialize();
    }

    // Generate: number = counter * mod + offset
    const number = this.counter * this.mod + this.offset;

    // Increment counter for next use
    this.counter++;

    // Update last generated
    this.lastGeneratedNumber = number;

    // Mark dirty for backend sync
    this.dirty = true;

    // Persist to IndexedDB (fire and forget)
    this.saveLocalState().catch(() => {});

    return String(number);
  }

  /**
   * Sync current state to backend.
   *
   * This saves the lastGeneratedNumber to the user's feature flags.
   */
  async syncToBackend(): Promise<void> {
    if (!this.dirty || this.lastGeneratedNumber === 0) {
      return;
    }

    try {
      await api.preOrders.saveNumberState(this.lastGeneratedNumber);
      this.dirty = false;
      this.lastBackendSync = Date.now();
    } catch (error) {
      // Keep dirty=true so we retry later
    }
  }

  /**
   * Force an immediate sync to backend.
   */
  async forceSync(): Promise<void> {
    // Force dirty to ensure sync happens
    if (this.lastGeneratedNumber > 0) {
      this.dirty = true;
    }
    await this.syncToBackend();
  }

  /**
   * Start periodic sync timer
   */
  private startPeriodicSync(): void {
    if (this.syncIntervalId) return;

    this.syncIntervalId = setInterval(() => {
      this.syncToBackend().catch(() => {});
    }, PreOrderNumberManager.SYNC_INTERVAL_MS);
  }

  /**
   * Set up visibility change handler to sync when tab is hidden
   */
  private setupVisibilityHandler(): void {
    if (typeof document === "undefined") return;

    this.visibilityHandler = () => {
      if (document.hidden && this.dirty) {
        // Tab is being hidden, sync state
        this.syncToBackend().catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  /**
   * Check if the manager is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Check if there are unsaved changes
   */
  isDirty(): boolean {
    return this.dirty;
  }

  /**
   * Get current state for debugging
   */
  getState(): {
    counter: number;
    lastGeneratedNumber: number;
    mod: number;
    offset: number;
    dirty: boolean;
    initialized: boolean;
  } {
    return {
      counter: this.counter,
      lastGeneratedNumber: this.lastGeneratedNumber,
      mod: this.mod,
      offset: this.offset,
      dirty: this.dirty,
      initialized: this.initialized,
    };
  }

  /**
   * Destroy the manager and clean up resources
   */
  destroy(): void {
    // Clear periodic sync
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }

    // Remove visibility handler
    if (this.visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }

    // Final sync attempt (non-blocking)
    if (this.dirty) {
      this.syncToBackend().catch(() => {});
    }

    // Reset state
    this.initialized = false;
    this.initPromise = null;
  }

  /**
   * Reset and re-initialize from backend.
   * Use this when you need to re-sync with server state.
   */
  async reset(): Promise<void> {
    // Clear local state
    try {
      await this.db.delete(STORES.METADATA, PreOrderNumberManager.STORAGE_KEY);
    } catch (error) {}

    // Clear config cache to force fresh fetch
    try {
      await this.db.delete(
        STORES.METADATA,
        PreOrderNumberManager.CONFIG_CACHE_KEY,
      );
    } catch (error) {}

    // Reset instance state
    this.counter = 0;
    this.lastGeneratedNumber = 0;
    this.dirty = false;
    this.initialized = false;
    this.initPromise = null;

    // Re-initialize

    await this.initialize();
  }
}

// Default values when feature flags are not set
export const DEFAULT_MOD = 10;
export const DEFAULT_OFFSET = 0;
